// A test user is treated as a Mastercard customer downstream when `mastercard` appears in the
// alias part of their email, so the option works by putting it there rather than by sending a flag
// of its own. That also means the email is the single source of truth: the checkbox reads its state
// back off the address, and editing the address by hand keeps the two in step.
const MASTERCARD_TAG = 'mastercard'

interface ParsedEmail {
  base: string
  segments: string[]
  domain: string
}

// Splits `base+one.two@domain` into its parts. Returns null for anything that is not yet a usable
// address -- a half-typed one is left exactly as the user has it.
const parse = (email: string): ParsedEmail | null => {
  const at = email.lastIndexOf('@')
  if (at <= 0 || at === email.length - 1) {
    return null
  }

  const local = email.slice(0, at)
  const domain = email.slice(at + 1)
  const plus = local.indexOf('+')

  if (plus < 0) {
    return { base: local, segments: [], domain }
  }

  return {
    base: local.slice(0, plus),
    segments: local
      .slice(plus + 1)
      .split('.')
      .filter(Boolean),
    domain,
  }
}

const rebuild = ({ base, segments, domain }: ParsedEmail): string =>
  segments.length > 0 ? `${base}+${segments.join('.')}@${domain}` : `${base}@${domain}`

export const hasMastercardAlias = (email: string): boolean => {
  const parsed = parse(email)
  return parsed?.segments.some(segment => segment.toLowerCase() === MASTERCARD_TAG) ?? false
}

// Adds or removes the tag, leaving every other part of the address alone -- the timestamp alias the
// autofill generates has to survive being toggled, or each toggle would hand back a different user.
export const withMastercardAlias = (email: string, enabled: boolean): string => {
  const parsed = parse(email)
  if (!parsed) {
    return email
  }

  const withoutTag = parsed.segments.filter(segment => segment.toLowerCase() !== MASTERCARD_TAG)

  return rebuild({
    ...parsed,
    segments: enabled ? [MASTERCARD_TAG, ...withoutTag] : withoutTag,
  })
}
